import { Dispatch, SetStateAction, useEffect } from 'react';
import { selectedReportsFilter } from '../../redux/reducers/filterReportsSlice';
import { useAppSelector } from '../../redux/hooks';
import { SkillResult } from '../../models/Skill';
import { useLazyLoadQuery } from 'react-relay';
import { StudentsSkillProgressDetailQuery } from '../../graphQLOperations/__generated__/StudentsSkillProgressDetailQuery.graphql';
import { studentSkillProgressDetailQuery } from '../../graphQLOperations/Students';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { Skill } from '../../models/SupportSkills';
import { SkillsAllTargetSkillTypesQuery } from '../../graphQLOperations/__generated__/SkillsAllTargetSkillTypesQuery.graphql';
import { getAllTargetSkillTypes } from '../../graphQLOperations/Skills';

interface Props {
  lazyLoading: boolean;
  setLazyLoading: Dispatch<SetStateAction<boolean>>;
  studentSkillProgressDetailInput?: {
    studentId: string;
    skillResult: SkillResult;
    count: number;
  };
  pageInfo:
    | {
        endCursor: string | null | undefined;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor: string | null | undefined;
      }
    | undefined;
  setPageInfo: Dispatch<
    SetStateAction<
      | {
          endCursor: string | null | undefined;
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          startCursor: string | null | undefined;
        }
      | undefined
    >
  >;

  setSkills: Dispatch<SetStateAction<Skill[]>>;
  fetchKeyScroll: number;
  setLastCall: Dispatch<SetStateAction<boolean>>;
}

const QueryStudentsProgressReportSkills = ({
  fetchKeyScroll,
  lazyLoading,
  pageInfo,
  setLastCall,
  setLazyLoading,
  setPageInfo,
  setSkills,
  studentSkillProgressDetailInput,
}: Props) => {
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);

  const { allTargetSkillTypes } = useLazyLoadQuery<SkillsAllTargetSkillTypesQuery>(
    getAllTargetSkillTypes,
    {},
    { fetchPolicy: 'network-only' },
  );

  const { studentSkillProgressDetail } = useLazyLoadQuery<StudentsSkillProgressDetailQuery>(
    studentSkillProgressDetailQuery,
    {
      first: 10,
      skip: lazyLoading && studentSkillProgressDetailInput === undefined,
      after: lazyLoading && pageInfo?.hasNextPage ? pageInfo?.endCursor : undefined,
      filter: {
        skillResult: studentSkillProgressDetailInput?.skillResult as SkillResult,
        studentId: studentSkillProgressDetailInput?.studentId
          ? `UserNode:${splitId(studentSkillProgressDetailInput?.studentId)}`
          : '',
        targetGroupIds: reportsFilterRedux?.roadmaps?.id ? [reportsFilterRedux?.roadmaps?.id] : [],
        standardGradeIds: reportsFilterRedux?.skillGrades?.length
          ? reportsFilterRedux?.skillGrades?.map(skill => skill?.code)
          : undefined,
        skillTypeIds: allTargetSkillTypes?.filter(type => type?.name !== 'Extension')?.map(type => type?.id),
      },
    },
    { fetchKey: fetchKeyScroll },
  );

  useEffect(() => {
    if (studentSkillProgressDetailInput && studentSkillProgressDetail && lazyLoading) {
      setPageInfo(studentSkillProgressDetail?.pageInfo);
      setLastCall(!studentSkillProgressDetail?.pageInfo?.hasNextPage);
      setLazyLoading(false);
      setSkills(prev => [...prev, ...(studentSkillProgressDetail?.nodes as Skill[])]);
    }
  }, [studentSkillProgressDetailInput, lazyLoading, studentSkillProgressDetail]);

  return <div style={{ display: 'none' }}></div>;
};

export default QueryStudentsProgressReportSkills;
